import { EmptyState } from "../../components/dashboard/emptyState";
import { MoneyProjectionSkeleton } from "../../components/dashboard/skeleton";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../utils/toast";
import { getGraphDetail } from "../../services/api";

export interface MoneyProjectionProps {
  eventId: string | number;
  eventName?: string;
  matchupCount?: number;
}

interface MoneyDetail {
  currency: string | null;
  projectedAmount?: number;
  fundedAmount?: number;
  netPaidAmount?: number;
  pendingAmount?: number;
  refundedAmount?: number;
  cancelledAmount?: number;
}

type MoneyKey =
  | "projectedAmount"
  | "fundedAmount"
  | "netPaidAmount"
  | "pendingAmount"
  | "refundedAmount"
  | "cancelledAmount";

interface FighterDetail {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  profileType?: string | null;
  profileImage?: string | null;
}

interface FighterMoneyDetail {
  corner: string;
  fighter?: FighterDetail | null;
  fighterName?: string | null;
  money?: MoneyDetail | null;
}

interface BoutDetail {
  matchupId: string | number;
  boutOrder?: number | null;
  boutName?: string | null;
  redCorner?: FighterDetail | null;
  redCornerFighterName?: string | null;
  blueCorner?: FighterDetail | null;
  blueCornerFighterName?: string | null;
  money?: MoneyDetail | null;
  fighterMoney?: FighterMoneyDetail[];
}

interface MoneyProjectionDetail {
  eventId?: string;
  eventName?: string;
  moneySummary?: MoneyDetail[];
  bouts?: BoutDetail[];
}

interface MoneyMetric {
  key: MoneyKey;
  label: string;
}

const readUsername = (): string => {
  try {
    const userJson = localStorage.getItem("loginResponse");
    if (!userJson) return "";

    const user = JSON.parse(userJson);
    return user.username || user.email?.split("@")[0] || "";
  } catch {
    return "";
  }
};

const MoneyProjection: React.FC<MoneyProjectionProps> = ({
  eventId,
  eventName,
  matchupCount,
}) => {
  const { t } = useTranslation("dashboard");
  const [isLoading, setIsLoading] = useState(false);
  const [moneyDetail, setMoneyDetail] = useState<MoneyProjectionDetail | null>(
    null,
  );
  const [selectedBoutId, setSelectedBoutId] = useState<string>("");
  const [isBoutListOpen, setIsBoutListOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement | null>(null);

  const metrics: MoneyMetric[] = [
    { key: "projectedAmount", label: t("projected", "Projected") },
    { key: "fundedAmount", label: t("funded", "Funded") },
    { key: "netPaidAmount", label: t("net_paid", "Net paid") },
    { key: "pendingAmount", label: t("pending", "Pending") },
    { key: "refundedAmount", label: t("refunded", "Refunded") },
    { key: "cancelledAmount", label: t("cancelled", "Cancelled") },
  ];

  const moneySummary = moneyDetail?.moneySummary || [];
  const bouts = moneyDetail?.bouts || [];
  const hasMoneyData = moneySummary.length > 0 || bouts.length > 0;

  const findSelectedBoutIndex = (): number => {
    for (let i = 0; i < bouts.length; i++) {
      if (String(bouts[i].matchupId) === selectedBoutId) return i;
    }
    return bouts.length > 0 ? 0 : -1;
  };

  const selectedBoutIndex = findSelectedBoutIndex();
  const selectedBout: BoutDetail | null =
    selectedBoutIndex >= 0 ? bouts[selectedBoutIndex] : null;

  const getAmount = (
    money: MoneyDetail | null | undefined,
    key: MoneyKey,
  ): number | null => {
    if (!money) return null;

    const amount = money[key];
    if (amount === undefined || amount === null) return 0;

    return Number(amount) || 0;
  };

  const formatMoneyAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) return "—";

    const safeAmount = Number.isFinite(Number(amount)) ? Number(amount) : 0;

    return safeAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getFighterName = (
    fighter?: FighterDetail | null,
    manualName?: string | null,
  ): string => {
    const firstName = fighter?.firstName || "";
    const lastName = fighter?.lastName || "";
    const fullName = (firstName + " " + lastName).trim();

    if (fullName) return fullName;

    const fallbackName = String(manualName || "").trim();
    if (fallbackName) return fallbackName;

    return fighter
      ? t("unknown_fighter", "Unknown fighter")
      : t("fighter_not_available", "Fighter not available");
  };

  const getInitials = (
    firstName?: string | null,
    lastName?: string | null,
    fallbackName?: string | null,
  ): string => {
    const first = firstName?.trim();
    const last = lastName?.trim();
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (first) {
      return first.slice(0, 2).toUpperCase();
    }
    if (fallbackName?.trim()) {
      const parts = fallbackName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0].charAt(0)}${parts[1].charAt(0)}`.toUpperCase();
      }
      return parts[0].slice(0, 2).toUpperCase();
    }
    return "?";
  };

  const getRedCornerName = (bout?: BoutDetail | null): string =>
    getFighterName(bout?.redCorner, bout?.redCornerFighterName);

  const getBlueCornerName = (bout?: BoutDetail | null): string =>
    getFighterName(bout?.blueCorner, bout?.blueCornerFighterName);

  const getBoutName = (bout?: BoutDetail | null, index?: number): string => {
    if (bout?.boutOrder !== undefined && bout.boutOrder !== null) {
      return t("bout", "Bout") + " " + bout.boutOrder;
    }

    if (bout?.boutName) return bout.boutName;
    if (typeof index === "number") return t("bout", "Bout") + " " + (index + 1);

    return t("bout", "Bout") + " -";
  };

  useEffect(() => {
    if (!eventId) return;

    let isPageActive = true;

    const loadMoneyDetail = async () => {
      setIsLoading(true);
      setMoneyDetail(null);
      setSelectedBoutId("");
      setIsBoutListOpen(false);

      try {
        const data = await getGraphDetail(readUsername(), String(eventId));
        if (!isPageActive) return;

        const detail = data as MoneyProjectionDetail;
        setMoneyDetail(detail);

        if (detail.bouts && detail.bouts.length > 0) {
          setSelectedBoutId(String(detail.bouts[0].matchupId));
        } else {
          setSelectedBoutId("");
        }
      } catch (error) {
        if (!isPageActive) return;

        let errorMessage = t(
          "failed_to_load_money_detail",
          "Failed to load money details",
        );

        if (axios.isAxiosError(error)) {
          errorMessage =
            error.response?.data?.message || error.message || errorMessage;
        } else if (error instanceof Error && error.message) {
          errorMessage = error.message;
        }

        setMoneyDetail(null);
        toast.error(errorMessage);
      } finally {
        if (isPageActive) setIsLoading(false);
      }
    };

    loadMoneyDetail();

    return () => {
      isPageActive = false;
    };
  }, [eventId, t]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!isBoutListOpen) return;
      if (!selectorRef.current) return;
      if (
        event.target instanceof Node &&
        !selectorRef.current.contains(event.target)
      ) {
        setIsBoutListOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isBoutListOpen]);

  const handleSelectBout = (matchupId: string | number) => {
    setSelectedBoutId(String(matchupId));
    setIsBoutListOpen(false);
  };

  const totalBoutsCount =
    bouts.length > 0
      ? bouts.length
      : matchupCount !== undefined && matchupCount !== null
      ? matchupCount
      : null;

  return (
    <div className="w-full text-left font-['Plus_Jakarta_Sans']">
      {isLoading ? (
        <MoneyProjectionSkeleton />
      ) : !moneyDetail || !hasMoneyData ? (
        <EmptyState
          text={t("no_money_projection", "No money projection available")}
        />
      ) : (
        <div className="flex flex-col justify-start items-start gap-4">
          <div className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="w-full flex flex-col justify-start items-start">
              <h2 className="text-foreground-foreground text-xl font-bold font-['Plus_Jakarta_Sans'] leading-7">
                {eventName || moneyDetail.eventName || t("event", "Event")}
              </h2>
            </div>
            <p className="text-slate-400 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4">
              {totalBoutsCount !== null
                ? `${totalBoutsCount} ${t("bouts", "bouts")}`
                : t(
                    "select_bout_funding",
                    "Select a bout to view its full funding breakdown",
                  )}
            </p>
          </div>

          {moneySummary.length > 0 && (
          <div className="self-stretch p-1 bg-slate-50/80 rounded-[20px] flex flex-col justify-start items-center border border-slate-100 overflow-hidden">
              <>
                <div className="hidden md:block w-full overflow-x-auto">
                  <div className="self-stretch px-4 py-2.5 inline-flex justify-center items-center min-w-[620px]">
                    <div className="flex-1 flex justify-start items-center gap-4">
                      <div className="flex-1 flex justify-start items-center gap-1">
                        <span className="text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                          {t("currency", "Currency")}
                        </span>
                      </div>
                      {metrics.map((metric) => (
                        <div
                          key={metric.key}
                          className="flex-1 flex justify-start items-center gap-1"
                        >
                          <span className="text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                            {metric.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="self-stretch bg-white rounded-2xl flex flex-col justify-start items-start divide-y divide-slate-100 min-w-[620px] shadow-sm">
                    {moneySummary.map((money, index) => (
                      <div
                        key={(money.currency || "no-currency") + "-" + index}
                        className="self-stretch min-h-14 px-4 py-3 inline-flex justify-center items-center"
                      >
                        <div className="flex-1 flex justify-start items-center gap-4">
                          <div className="flex-1 flex justify-start items-center gap-2">
                            <span className="text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5">
                              {money.currency || t("all_bouts", "All bouts")}
                            </span>
                          </div>
                          {metrics.map((metric) => (
                            <div
                              key={metric.key}
                              className="flex-1 flex justify-start items-center gap-2"
                            >
                              <span
                                className={`text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 tabular-nums ${
                                  metric.key === "refundedAmount"
                                    ? "text-emerald-600"
                                    : metric.key === "cancelledAmount"
                                    ? "text-rose-600"
                                    : "text-slate-900"
                                }`}
                              >
                                {formatMoneyAmount(getAmount(money, metric.key))}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="md:hidden w-full flex flex-col gap-3 p-1">
                  {moneySummary.map((money, index) => (
                    <div
                      key={`mobile-summary-${money.currency || "all"}-${index}`}
                      className="w-full bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm"
                    >
                      <div className="mb-2">
                        <span className="text-xs text-slate-400 font-normal block leading-4">
                          {t("currency", "Currency")}
                        </span>
                        <span className="text-sm font-bold text-slate-900 leading-5">
                          {money.currency || t("all_bouts", "All bouts")}
                        </span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {metrics.map((metric) => (
                          <div
                            key={metric.key}
                            className="py-2.5 flex items-center justify-between text-xs"
                          >
                            <span className="text-slate-400 font-normal leading-4">
                              {metric.label}
                            </span>
                            <span
                              className={`font-medium tabular-nums ${
                                metric.key === "refundedAmount"
                                  ? "text-emerald-600"
                                  : metric.key === "cancelledAmount"
                                  ? "text-rose-600"
                                  : "text-slate-900"
                              }`}
                            >
                              {formatMoneyAmount(getAmount(money, metric.key))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
          </div>
          )}

          {selectedBout && (
            <div
              className="self-stretch pb-4 relative flex flex-col justify-start items-start gap-3 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden"
              ref={selectorRef}
            >
              <div
                onClick={() => {
                  if (bouts.length > 1) {
                    setIsBoutListOpen((prev) => !prev);
                  }
                }}
                className={`hidden md:inline-flex self-stretch px-4 py-2.5 bg-gradient-to-r from-blue-100 via-white via-40% to-rose-100 rounded-tl-3xl rounded-tr-3xl border-b border-slate-200 justify-start items-center gap-2.5 overflow-hidden ${
                  bouts.length > 1 ? "cursor-pointer select-none" : ""
                }`}
              >
                <div className="flex-1 min-h-9 px-3 py-1.5 rounded-[20px] flex justify-start items-center gap-3">
                  <div className="size-9 shrink-0 overflow-hidden rounded-full bg-slate-100 border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 text-xs font-bold select-none">
                    {selectedBout.blueCorner?.profileImage ? (
                      <img
                        src={selectedBout.blueCorner.profileImage}
                        alt={getBlueCornerName(selectedBout)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>
                        {getInitials(
                          selectedBout.blueCorner?.firstName,
                          selectedBout.blueCorner?.lastName,
                          getBlueCornerName(selectedBout),
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 inline-flex flex-col justify-start items-start">
                    <span className="self-stretch justify-start text-blue-600 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4">
                      {t("blue_corner", "Blue corner")}
                    </span>
                    <span className="self-stretch justify-start text-slate-900 text-base font-bold font-['Plus_Jakarta_Sans'] leading-6 truncate">
                      {getBlueCornerName(selectedBout)}
                    </span>
                  </div>
                </div>

                <div className="inline-flex flex-col justify-center items-center gap-2 px-2 shrink-0">
                  <div className="w-24 h-3 max-h-3 rounded-sm inline-flex justify-center items-center">
                    <div className="flex-1 h-0 border-t border-slate-200" />
                    <div className="px-3 flex justify-center items-center gap-2">
                      <span className="justify-start text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                        {t("versus", "Versus")}
                      </span>
                    </div>
                    <div className="flex-1 h-0 border-t border-slate-200" />
                  </div>
                </div>

                <div className="flex-1 min-h-9 px-3 py-1.5 rounded-[20px] flex justify-end items-center gap-3">
                  <div className="flex-1 inline-flex flex-col justify-start items-end">
                    <span className="self-stretch text-right justify-start text-rose-600 text-xs font-normal font-['Plus_Jakarta_Sans'] leading-4">
                      {t("red_corner", "Red corner")}
                    </span>
                    <span className="self-stretch text-right justify-start text-slate-900 text-base font-bold font-['Plus_Jakarta_Sans'] leading-6 truncate">
                      {getRedCornerName(selectedBout)}
                    </span>
                  </div>
                  <div className="size-9 shrink-0 overflow-hidden rounded-full bg-slate-100 border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 text-xs font-bold select-none">
                    {selectedBout.redCorner?.profileImage ? (
                      <img
                        src={selectedBout.redCorner.profileImage}
                        alt={getRedCornerName(selectedBout)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>
                        {getInitials(
                          selectedBout.redCorner?.firstName,
                          selectedBout.redCorner?.lastName,
                          getRedCornerName(selectedBout),
                        )}
                      </span>
                    )}
                  </div>
                  {bouts.length > 1 && (
                    <svg
                      className={`w-4 h-4 text-slate-900 transition-transform ml-1 ${
                        isBoutListOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </div>
              </div>

              <div
                onClick={() => {
                  if (bouts.length > 1) {
                    setIsBoutListOpen((prev) => !prev);
                  }
                }}
                className={`md:hidden self-stretch px-4 py-3 bg-gradient-to-r from-blue-100 via-white via-50% to-rose-100 rounded-t-3xl border-b border-slate-200 flex justify-between items-center gap-2 overflow-hidden ${
                  bouts.length > 1 ? "cursor-pointer select-none" : ""
                }`}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="size-9 shrink-0 overflow-hidden rounded-full bg-slate-100 border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 text-xs font-bold select-none">
                    {selectedBout.blueCorner?.profileImage ? (
                      <img
                        src={selectedBout.blueCorner.profileImage}
                        alt={getBlueCornerName(selectedBout)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>
                        {getInitials(
                          selectedBout.blueCorner?.firstName,
                          selectedBout.blueCorner?.lastName,
                          getBlueCornerName(selectedBout),
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col justify-start items-start min-w-0">
                    <span className="text-blue-600 text-xs font-normal leading-4 truncate">
                      {t("blue_corner", "Blue corner")}
                    </span>
                    <span className="text-slate-900 text-sm font-bold leading-5 truncate max-w-[100px] xs:max-w-[130px] sm:max-w-none">
                      {getBlueCornerName(selectedBout)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2.5 min-w-0">
                  <div className="flex flex-col justify-start items-end min-w-0">
                    <span className="text-rose-600 text-xs font-normal leading-4 truncate text-right">
                      {t("red_corner", "Red corner")}
                    </span>
                    <span className="text-slate-900 text-sm font-bold leading-5 truncate max-w-[100px] xs:max-w-[130px] sm:max-w-none text-right">
                      {getRedCornerName(selectedBout)}
                    </span>
                  </div>
                  <div className="size-9 shrink-0 overflow-hidden rounded-full bg-slate-100 border border-slate-200 shadow-sm flex items-center justify-center text-slate-700 text-xs font-bold select-none">
                    {selectedBout.redCorner?.profileImage ? (
                      <img
                        src={selectedBout.redCorner.profileImage}
                        alt={getRedCornerName(selectedBout)}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>
                        {getInitials(
                          selectedBout.redCorner?.firstName,
                          selectedBout.redCorner?.lastName,
                          getRedCornerName(selectedBout),
                        )}
                      </span>
                    )}
                  </div>
                  {bouts.length > 1 && (
                    <svg
                      className={`w-4 h-4 text-slate-700 transition-transform shrink-0 ml-1 ${
                        isBoutListOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {isBoutListOpen && bouts.length > 1 && (
                <div className="self-stretch px-4 py-2 border-b border-slate-100 bg-slate-50/80 flex flex-wrap gap-2">
                  {bouts.map((b, idx) => (
                    <button
                      key={b.matchupId}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectBout(b.matchupId);
                      }}
                      className={`px-3 py-1.5 rounded-2xl text-xs font-medium font-['Plus_Jakarta_Sans'] transition-all ${
                        String(b.matchupId) === String(selectedBout.matchupId)
                          ? "bg-slate-900 text-white shadow-sm"
                          : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-100"
                      }`}
                    >
                      {getBoutName(b, idx)}: {getRedCornerName(b)} vs{" "}
                      {getBlueCornerName(b)}
                    </button>
                  ))}
                </div>
              )}

              <div className="self-stretch px-4 flex flex-col justify-center items-start gap-3 w-full">
                <div className="hidden md:flex self-stretch p-1 bg-slate-50/80 rounded-[20px] flex-col justify-start items-center border border-slate-100 overflow-hidden w-full">
                  <div className="w-full overflow-x-auto">
                    <div className="self-stretch px-4 py-2.5 inline-flex justify-center items-center min-w-[620px]">
                      <div className="flex-1 flex justify-start items-center gap-4">
                        <div className="flex-1 flex justify-start items-center gap-1">
                          <span className="text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                            {t("breakdown", "Breakdown")}
                          </span>
                        </div>
                        {metrics.map((metric) => (
                          <div
                            key={metric.key}
                            className="flex-1 flex justify-start items-center gap-1"
                          >
                            <span className="text-slate-400 text-xs font-medium font-['Plus_Jakarta_Sans'] leading-4">
                              {metric.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="self-stretch bg-white rounded-2xl flex flex-col justify-start items-start divide-y divide-slate-100 min-w-[620px] shadow-sm">
                      <div className="self-stretch min-h-14 px-4 py-3 inline-flex justify-center items-center">
                        <div className="flex-1 flex justify-start items-center gap-4">
                          <div className="flex-1 flex justify-start items-center gap-2">
                            <span className="text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5">
                              {t("bout_total", "Bout total")}
                            </span>
                          </div>
                          {metrics.map((metric) => (
                            <div
                              key={metric.key}
                              className="flex-1 flex justify-start items-center gap-2"
                            >
                              <span
                                className={`text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 tabular-nums ${
                                  metric.key === "refundedAmount"
                                    ? "text-emerald-600"
                                    : metric.key === "cancelledAmount"
                                    ? "text-rose-600"
                                    : "text-slate-900"
                                }`}
                              >
                                {formatMoneyAmount(
                                  getAmount(selectedBout.money, metric.key),
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {(selectedBout.fighterMoney || []).map(
                        (fighterMoney, index) => {
                          const corner = String(
                            fighterMoney.corner || "",
                          ).toUpperCase();
                          const isRed = corner === "RED";
                          const cornerLabel = isRed
                            ? t("red_corner", "red corner")
                            : t("blue_corner", "blue corner");
                          const fighterName = getFighterName(
                            fighterMoney.fighter,
                            fighterMoney.fighterName,
                          );
                          const fighterId =
                            fighterMoney.fighter?.id ||
                            fighterMoney.fighterName ||
                            index;
                          const rowKey = `${selectedBout.matchupId}-${corner}-${fighterId}`;

                          return (
                            <div
                              key={rowKey}
                              className="self-stretch min-h-14 px-4 py-3 inline-flex justify-center items-center"
                            >
                              <div className="flex-1 flex justify-start items-center gap-4">
                                <div className="flex-1 flex justify-start items-center gap-2">
                                  <span className="text-slate-900 text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5">
                                    {fighterName} ({cornerLabel})
                                  </span>
                                </div>
                                {metrics.map((metric) => (
                                  <div
                                    key={metric.key}
                                    className="flex-1 flex justify-start items-center gap-2"
                                  >
                                    <span
                                      className={`text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 tabular-nums ${
                                        metric.key === "refundedAmount"
                                          ? "text-emerald-600"
                                          : metric.key === "cancelledAmount"
                                          ? "text-rose-600"
                                          : "text-slate-900"
                                      }`}
                                    >
                                      {formatMoneyAmount(
                                        getAmount(
                                          fighterMoney.money,
                                          metric.key,
                                        ),
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                </div>

                <div className="md:hidden w-full flex flex-col gap-3">
                  <div className="w-full bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm">
                    <div className="mb-2">
                      <span className="text-xs text-slate-400 font-normal block leading-4">
                        {t("breakdown", "Breakdown")}
                      </span>
                      <span className="text-sm font-bold text-slate-900 leading-5">
                        {t("bout_total", "Bout total")}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {metrics.map((metric) => (
                        <div
                          key={metric.key}
                          className="py-2.5 flex items-center justify-between text-xs"
                        >
                          <span className="text-slate-400 font-normal leading-4">
                            {metric.label}
                          </span>
                          <span
                            className={`font-medium tabular-nums ${
                              metric.key === "refundedAmount"
                                ? "text-emerald-600"
                                : metric.key === "cancelledAmount"
                                ? "text-rose-600"
                                : "text-slate-700"
                            }`}
                          >
                            {formatMoneyAmount(
                              getAmount(selectedBout.money, metric.key),
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {(selectedBout.fighterMoney || []).map((fighterMoney, index) => {
                    const corner = String(
                      fighterMoney.corner || "",
                    ).toUpperCase();
                    const isRed = corner === "RED";
                    const cornerLabel = isRed
                      ? t("red_corner", "red corner")
                      : t("blue_corner", "blue corner");
                    const fighterName = getFighterName(
                      fighterMoney.fighter,
                      fighterMoney.fighterName,
                    );
                    const fighterId =
                      fighterMoney.fighter?.id ||
                      fighterMoney.fighterName ||
                      index;
                    const cardKey = `mobile-card-${selectedBout.matchupId}-${corner}-${fighterId}`;

                    return (
                      <div
                        key={cardKey}
                        className="w-full bg-white rounded-2xl border border-slate-200/80 p-4 shadow-sm"
                      >
                        <div className="mb-2">
                          <span className="text-xs text-slate-400 font-normal block leading-4">
                            {t("breakdown", "Breakdown")}
                          </span>
                          <span className="text-sm font-bold text-slate-900 leading-5">
                            {fighterName} ({cornerLabel})
                          </span>
                        </div>
                        <div className="divide-y divide-slate-100">
                          {metrics.map((metric) => (
                            <div
                              key={metric.key}
                              className="py-2.5 flex items-center justify-between text-xs"
                            >
                              <span className="text-slate-400 font-normal leading-4">
                                {metric.label}
                              </span>
                              <span
                                className={`font-medium tabular-nums ${
                                  metric.key === "refundedAmount"
                                    ? "text-emerald-600"
                                    : metric.key === "cancelledAmount"
                                    ? "text-rose-600"
                                    : "text-slate-700"
                                }`}
                              >
                                {formatMoneyAmount(
                                  getAmount(fighterMoney.money, metric.key),
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MoneyProjection;
